import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { STICKERS_PAR_PAGE } from '../stickers';
import { playBark, STICKER_JOY_EVENT } from '../easterEggs';
import './Stickers.css';

// Petit générateur pseudo-aléatoire : les stickers gardent la même place
// à chaque visite au lieu de bouger à chaque rechargement.
function seededRandom(seed) {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

// « /images/stickers/sticker-cisco2.png » → « cisco2 »
const nameOf = (src) => src.match(/sticker-([\w-]+)\.\w+$/)?.[1] ?? '';

function Sticker({ src, style }) {
  const name = nameOf(src);
  const bodyRef = useRef(null);
  const drag = useRef(null);
  const hovers = useRef(0);
  const bubbleTimer = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [bubble, setBubble] = useState(null);
  const [drops, setDrops] = useState([]);
  const [driving, setDriving] = useState(false);
  const [joy, setJoy] = useState(false);

  useEffect(() => {
    const onJoy = (event) => {
      if (event.detail === name) {
        setJoy(true);
        setTimeout(() => setJoy(false), 1300);
      }
    };
    window.addEventListener(STICKER_JOY_EVENT, onJoy);
    return () => window.removeEventListener(STICKER_JOY_EVENT, onJoy);
  }, [name]);

  const showBubble = (text, duration) => {
    setBubble({ text, id: Date.now() });
    clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => setBubble(null), duration);
  };

  const drive = () => {
    const body = bodyRef.current;
    if (driving || !body) {
      return;
    }
    const out = body.getBoundingClientRect().right + 60;
    setDriving(true);
    // Démarre en accélérant vers la gauche, puis revient tout près de sa place
    // (au lieu de retraverser l'écran) et freine avec un petit rebond.
    const animation = body.animate(
      [
        { transform: 'translateX(0)', easing: 'ease-out' },
        { transform: 'translateX(10px)', offset: 0.08, easing: 'ease-in' },
        { transform: `translateX(${-out}px)`, offset: 0.45, opacity: 1 },
        { transform: `translateX(${-out}px)`, offset: 0.55, opacity: 0 },
        { transform: 'translateX(160px)', offset: 0.56, opacity: 0, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' },
        { transform: 'translateX(-8px)', offset: 0.9, opacity: 1, easing: 'ease-in-out' },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: 3000 }
    );
    animation.onfinish = () => setDriving(false);
  };

  const handleClick = () => {
    if (name.startsWith('cisco')) {
      playBark();
      showBubble('Wouf\u00A0!', 800);
    } else if (name === 'van') {
      drive();
    }
  };

  const handlePointerEnter = (event) => {
    if (name !== 'flasque' || event.pointerType !== 'mouse') {
      return;
    }
    hovers.current += 1;
    if (hovers.current >= 3) {
      const id = Date.now();
      setDrops((prev) => [...prev, id]);
      setTimeout(() => setDrops((prev) => prev.filter((d) => d !== id)), 1400);
    }
  };

  // Glisser-déposer : un clic sans mouvement reste un clic.
  const handlePointerDown = (event) => {
    if (event.button !== 0 || driving) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { px: event.clientX, py: event.clientY, x: offset.x, y: offset.y, moved: false };
  };

  const handlePointerMove = (event) => {
    const d = drag.current;
    if (!d) {
      return;
    }
    const dx = event.clientX - d.px;
    const dy = event.clientY - d.py;
    if (!d.moved && Math.hypot(dx, dy) < 5) {
      return;
    }
    d.moved = true;
    setDragging(true);
    setOffset({ x: d.x + dx, y: d.y + dy });
  };

  const handlePointerUp = () => {
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    if (d && !d.moved) {
      handleClick();
    }
  };

  const handlePointerCancel = () => {
    drag.current = null;
    setDragging(false);
  };

  const classes = [
    'sticker',
    dragging && 'sticker-dragging',
    driving && 'sticker-driving',
    joy && 'sticker-joy',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      style={{ ...style, translate: `${offset.x}px ${offset.y}px` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerEnter={handlePointerEnter}
    >
      <div className="sticker-body" ref={bodyRef}>
        <img src={src} alt="" draggable="false" />
        {driving &&
          [0, 1, 2].map((i) => (
            <span key={i} className="sticker-smoke" style={{ animationDelay: `${i * 0.25}s` }} />
          ))}
      </div>
      {bubble && (
        <span key={bubble.id} className="sticker-bubble">
          {bubble.text}
        </span>
      )}
      {drops.map((id) =>
        [0, 1, 2].map((i) => (
          <span
            key={`${id}-${i}`}
            className="sticker-drop"
            style={{ left: `${40 + i * 10}%`, animationDelay: `${i * 0.2}s` }}
          />
        ))
      )}
    </div>
  );
}

function Stickers({ images, seed = 0 }) {
  return (
    <div className="stickers" aria-hidden="true">
      {images.map((image, i) => {
        // Une entrée peut être un simple chemin ou { src, scale, top, offset }
        // pour agrandir un sticker ou forcer sa position (en % de la page / du bord).
        const { src, scale = 1, top, offset } = typeof image === 'string' ? { src: image } : image;
        const rand = (n) => seededRandom(seed * 1000 + i * 10 + n);
        // Alterne gauche / droite pour rester sur les bords, loin des cards.
        const side = i % 2 === 0 ? 'left' : 'right';
        const style = {
          top: `${top ?? ((i + rand(1)) / images.length) * 90 + 2}%`,
          [side]: `${offset ?? rand(2) * 12 + 1}%`,
          width: `${Math.round((rand(3) * 70 + 130) * scale)}px`,
          rotate: `${Math.round(rand(4) * 30 - 15)}deg`,
        };
        return <Sticker key={src} src={src} style={style} />;
      })}
    </div>
  );
}

export function PageStickers() {
  const { pathname } = useLocation();
  const images = STICKERS_PAR_PAGE[pathname];
  if (!images) {
    return null;
  }
  const seed = Object.keys(STICKERS_PAR_PAGE).indexOf(pathname);
  // key : repart de zéro à chaque changement de page.
  return <Stickers key={pathname} images={images} seed={seed} />;
}

export default Stickers;
